import { Card, Image, Row, Col, Button, Spin } from 'antd';
import PropTypes from 'prop-types';
import { EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { DescriptionList } from '../../../../../components';
import { supervisionAnnouncementInfo } from '../listContent/supervisionAnnouncementInfo';
import { useAuthContext } from '../../../../../contexts/AuthContext';
import { CardTitle } from '../../../../../components/CardTitle/CardTitle';

const { PreviewGroup } = Image;

/**
 * Renders a card component for displaying supervision announcements.
 * @component
 *
 * @param {Object} props - The component props.
 * @param {Object} props.anounce - The announcement object.
 * @param {Function} props.setAnounceModalVisible - A function to set the visibility of the announcement modal.
 * @param {Object} props.supervision - The supervision object.
 * @param {boolean} props.isLoading - A flag indicating if the component is in a loading state.
 * @returns {JSX.Element} The rendered SupervisionAnnouncementCard component.
 */
export const SupervisionAnnouncementCard = ({
  anounce,
  setAnounceModalVisible,
  supervision,
  isLoading
}) => {
  const { t } = useTranslation();
  const { token } = useAuthContext();

  const { summary: { anounce_photos } = {} } = supervision || {};

  return (
    <Spin spinning={isLoading}>
      <Card
        title={
          <CardTitle icon="calendar">
            {t('supervisions.show.anounce.title')}
          </CardTitle>
        }
        extra={
          [
            'REQUEST',
            'BOOKED',
            'PUBLISHED',
            'RESERVE',
            'WAITING_BOOKING'
          ].includes(supervision?.status) ? (
            <Button type="link" onClick={() => setAnounceModalVisible(true)}>
              <EditOutlined />
            </Button>
          ) : null
        }
      >
        <Row gutter={[12, 12]}>
          <Col xs={24} lg={8} className="supervision-img-wrapper">
            <PreviewGroup>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8
                }}
              >
                {anounce_photos?.map((photo) => (
                  <Image
                    key={photo}
                    src={`${process.env.REACT_APP_API_URL}/get-file-url/${photo}/${token}`}
                    width={120}
                    height={120}
                    style={{ objectFit: 'cover' }}
                    placeholder={<Spin loading size="large" />}
                  />
                ))}
              </div>
            </PreviewGroup>
          </Col>
          <Col xs={24} lg={16}>
            <DescriptionList
              data={supervisionAnnouncementInfo({ anounce, supervision })}
              translate
            />
          </Col>
        </Row>
      </Card>
    </Spin>
  );
};

SupervisionAnnouncementCard.propTypes = {
  anounce: PropTypes.shape({}).isRequired,
  setAnounceModalVisible: PropTypes.func.isRequired,
  supervision: PropTypes.shape({
    status: PropTypes.string
  }).isRequired,
  isLoading: PropTypes.bool.isRequired
};
